import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { DataTable, DateRangeFilter, ExportButtons, PageHeader, StatusBadge } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REPORTS = {
  sales: {
    label: "Sales Report", endpoint: "/reports/sales", dated: true,
    columns: [
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "invoice_no", label: "Invoice" }, { key: "customer", label: "Customer" },
      { key: "customer_type", label: "Type" },
      { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
      { key: "gst", label: "GST", align: "right", render: (r) => <Money value={r.gst} /> },
      { key: "grand_total", label: "Total", align: "right", render: (r) => <Money value={r.grand_total} /> },
      { key: "paid", label: "Paid", align: "right", render: (r) => <Money value={r.paid} /> },
      { key: "balance", label: "Balance", align: "right", render: (r) => <Money value={r.balance} /> },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    ],
  },
  expenses: {
    label: "Expense Report", endpoint: "/reports/expenses", dated: true,
    columns: [
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "vendor", label: "Vendor" }, { key: "category", label: "Category" },
      { key: "description", label: "Description", render: (r) => <span className="line-clamp-1 max-w-56">{r.description}</span> },
      { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
      { key: "gst", label: "GST", align: "right", render: (r) => <Money value={r.gst} /> },
      { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} /> },
      { key: "itc", label: "ITC" }, { key: "mode", label: "Mode" },
    ],
  },
  collections: {
    label: "Payment Collections", endpoint: "/reports/collections", dated: true,
    columns: [
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "receipt_no", label: "Receipt" }, { key: "customer", label: "Customer" },
      { key: "invoice_no", label: "Invoice" }, { key: "mode", label: "Mode" },
      { key: "reference", label: "Reference" },
      { key: "amount", label: "Amount", align: "right", render: (r) => <Money value={r.amount} /> },
    ],
  },
  daily_collection: {
    label: "Daily Collection", endpoint: "/reports/daily-collection", dated: true,
    columns: [
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "count", label: "Receipts", align: "right" },
      { key: "cash", label: "Cash", align: "right", render: (r) => <Money value={r.cash} /> },
      { key: "bank", label: "Bank/UPI/Card", align: "right", render: (r) => <Money value={r.bank} /> },
      { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} className="font-semibold" /> },
    ],
  },
  profitability: {
    label: "Profitability (Bookings)", endpoint: "/reports/profitability", dated: true,
    columns: [
      { key: "booking_id", label: "Booking" }, { key: "customer", label: "Customer" },
      { key: "package", label: "Package" }, { key: "destination", label: "Destination" },
      { key: "revenue", label: "Net Revenue", align: "right", render: (r) => <Money value={r.revenue} /> },
      { key: "cost", label: "Est. Cost", align: "right", render: (r) => <Money value={r.cost} /> },
      { key: "profit", label: "Gross Profit", align: "right", render: (r) => (
        <span className={`num font-semibold ${r.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{inr(r.profit)}</span>) },
      { key: "margin", label: "Margin", align: "right", render: (r) => <span className="num">{r.margin}%</span> },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    ],
  },
  package_sales: {
    label: "Package Sales", endpoint: "/reports/package-sales", dated: true,
    columns: [
      { key: "package", label: "Package" }, { key: "invoices", label: "Invoices", align: "right" },
      { key: "taxable", label: "Taxable", align: "right", render: (r) => <Money value={r.taxable} /> },
      { key: "total", label: "Revenue", align: "right", render: (r) => <Money value={r.total} className="font-semibold" /> },
    ],
  },
  destination_sales: {
    label: "Destination Sales", endpoint: "/reports/destination-sales", dated: true,
    columns: [
      { key: "destination", label: "Destination" }, { key: "bookings", label: "Bookings", align: "right" },
      { key: "revenue", label: "Revenue", align: "right", render: (r) => <Money value={r.revenue} className="font-semibold" /> },
    ],
  },
  booking_register: {
    label: "Booking Register", endpoint: "/reports/booking-register", dated: true,
    columns: [
      { key: "booking_id", label: "ID" }, { key: "customer", label: "Customer" },
      { key: "agent", label: "Agent", render: (r) => r.agent || "Direct" },
      { key: "package", label: "Package" },
      { key: "start_date", label: "Travel", render: (r) => fmtDate(r.start_date) },
      { key: "travellers", label: "Pax", align: "right" },
      { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} /> },
      { key: "balance", label: "Balance", align: "right", render: (r) => <Money value={r.balance} /> },
      { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    ],
  },
};

export default function Reports() {
  const [key, setKey] = useState("sales");
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const cfg = REPORTS[key];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(cfg.endpoint, { params: { from_date: range.from, to_date: range.to } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [cfg, range]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports Center" subtitle="Business, collection & profitability reports with export"
        testid="reports-header"
        actions={
          <>
            <Select value={key} onValueChange={setKey}>
              <SelectTrigger className="w-56 bg-white border-[#E5E0D8]" data-testid="report-select">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REPORTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="report-date-filter" />
            <ExportButtons base="/reports/export" params={{ report: key, from_date: range.from, to_date: range.to }} testid="report-export" />
            <button onClick={() => window.print()} className="text-xs font-semibold text-[#0D4F4A] hover:underline" data-testid="report-print">Print</button>
          </>
        } />
      {data?.totals && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-sm bg-white border border-[#E5E0D8] rounded-lg px-4 py-2.5" data-testid="report-totals">
          {Object.entries(data.totals).map(([k, v]) => (
            <span key={k} className="text-[#5C6B69]">{k.replace(/_/g, " ")}: <b className="num text-[#0D4F4A]">{inr(v)}</b></span>
          ))}
        </div>
      )}
      {loading && !data ? (
        <div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-[#5C6B69]" /></div>
      ) : (
        <DataTable testid={`report-${key}`} columns={cfg.columns} rows={data?.rows || []}
          loading={loading} empty="No data in this period" />
      )}
      {data?.by_category && (
        <div className="mt-4 text-sm bg-white border border-[#E5E0D8] rounded-lg px-4 py-3">
          <b className="text-xs uppercase tracking-wide text-[#5C6B69]">By category:</b>
          <span className="ml-3 space-x-4">
            {data.by_category.map((c) => <span key={c.category}>{c.category} <b className="num">{inr(c.total)}</b></span>)}
          </span>
        </div>
      )}
    </div>
  );
}
